import React, { useState, useEffect } from "react";
import { Input } from "antd";

interface BufferedTextInputProps {
  value: string;
  onCommit: (value: string) => void;
  addonBefore?: string;
  placeholder?: string;
  style?: React.CSSProperties;
}

/**
 * A controlled text `<Input>` that only commits to the parent on blur, not on
 * every keystroke — mirrors `NumberInput.tsx`'s own local-buffer-then-commit
 * pattern. Use this instead of a plain `<Input>` for any per-row editable
 * text cell in a large table (e.g. Plot No/Housetype/Foundation Type): a
 * plain `<Input>` whose `onChange` calls straight into a table's row-update
 * handler recomputes filtered data/totals/columns on every character typed,
 * which is what made typing feel slow before this existed (see CutFills.tsx's
 * CHANGELOG entry). Every numeric column across these tables already avoids
 * this via `NumberInput`; this is the text-column equivalent.
 */
const BufferedTextInput: React.FC<BufferedTextInputProps> = React.memo(
  function BufferedTextInput({ value, onCommit, addonBefore, placeholder, style }) {
    const [localValue, setLocalValue] = useState(value);
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
      if (!isFocused) setLocalValue(value);
    }, [value, isFocused]);

    return (
      <Input
        addonBefore={addonBefore}
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          setIsFocused(false);
          if (localValue !== value) onCommit(localValue);
        }}
        placeholder={placeholder}
        style={style}
      />
    );
  },
);

export default BufferedTextInput;
